import React, { useState, useEffect, useRef, useCallback } from "react";
import { colors2 } from "oolib";
import { valueNotFalsyOrEmptyAryObj } from "../../utils/validation/validatorFns";
import FilterModule from "../../components/discovery/FilterModule";
import { useShowFilterModuleV3 } from "../../components/discovery/FilterModule/hooks/useShowFilterModuleV3";
import { getSavedFilterConfigKey } from "../../components/discovery/FilterModule/utils/useSavedFilterConfig";
import { getTenant } from "@/TENANT_CONFIGS";
import ListingModule from "./comps/ListingModule";
import ErrorStates from "../../components/ErrorStates";
import { ActionsPanel } from "./newComps/ActionsPanel";
import { Shell } from "./newComps/Shell";
import { FilterModuleHeader } from "./newComps/Shell/comps/FiltersSidebarShell/comps/FilterModuleHeader";
import { ONLY_DESKTOP_ACTIONS_PANEL_HEIGHT } from "./newComps/ActionsPanel/styled";

export const ListingLayout = ({
  // Filter props
  totalCount,
  setActiveFilters,
  activeFilters,
  presetActiveFilters,
  filterConfig,
  contentTypes,
  
  // ListingModule props
  listingStyle,
  contentError,
  contentStatus,
  contentData,
  emptyPageConfigSearch,
  sectionTitle,
  sectionLink,
  showCount,
  listingConfig,
  fetchNextPage,
  contentIsFetchingNextPage,
  hasNextPage,
  ListingWrapper,
  enableContainerQuery,
  inTCI,
  builderProps,
  getDataToBeExported,
  
  // Actions panel props
  actionRef,
  isMobile,
  actionsPanelStickyTop = 0,
  /**
   * sticky top to viewport gap is essential.
   * basically, this defines, the gap between the viewport and the top 
   * of the container (with regards to the viewport), within which we want our
   * layout to stick.
   * 
   * for example, in the case of a typical listing page. this gap is 0, since
   * we want the listing page to stick to the top of the viewport
   * 
   * but in My Content, we want the listing layout to stick within the right scrollable area
   * below the primary navbar. so in this case, the stickyTopToViewportTopGap would essentially 
   * be the header height
   * 
   */
  stickyTopToViewportTopGap = 0,
  coverConfigExists = false,
  
  // Sort related props
  showSortDropdown,
  activeSort,
  setActiveSort,
  sortConfig,
  
  // Search related props
  showSearchInput,
  searchTermInputText,
  handleSearch,

  // Listing style switcher related
  enableListingStyleSwitcher,
  setListingStyle,
  collectionId
}) => {
  const showFilters = valueNotFalsyOrEmptyAryObj(filterConfig);

  const ONLY_DESKTOP_filtersSidebarStickyTop = actionsPanelStickyTop + ONLY_DESKTOP_ACTIONS_PANEL_HEIGHT; //fairly self explanatory. filter sidebar will always stick at the bottom edge of the actions panel

  const { showFilterModuleV3 } = useShowFilterModuleV3();

  const [showSideBarLeft, setShowSideBarLeft] = useState(() => {
    if (isMobile) return false;
    if (showFilterModuleV3) {
      // Self-managed tenants: open sidebar by default only when saved filter config exists
      const key = getSavedFilterConfigKey(getTenant(), "ListingPage", collectionId);
      try {
        const stored = JSON.parse(localStorage.getItem(key));
        return Array.isArray(stored) && stored.length > 0;
      } catch {
        return false;
      }
    }
    return true; // non-self-managed desktop: open by default
  });

  // External control for LightboxFilterHeader (self-managed tenants)
  const [addFilterAnchorEl, setAddFilterAnchorElRaw] = useState(null);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [clearFilters, setClearFilters] = useState(null);
  const [hasFiltersToClear, setHasFiltersToClear] = useState(false);
  const onClearFilters = useCallback((fn) => setClearFilters(() => fn), []);

  // Keep the Add Filter popover open when the FIRST filter is applied.
  // Applying the first filter flips hasFiltersToClear + auto-opens the sidebar,
  // which re-lays-out FilterModuleHeader (collapsed "Add Filter" button → sidebar
  // header). That briefly unmounts the popover's anchor and fires
  // setAddFilterAnchorEl(null); a null anchor flips LightboxFilterHeader out of
  // anchored mode and unmounts the open popover — so it closed on the first pick
  // but stayed open on later picks (anchor already stable). While the popover is
  // open, ignore null-anchor updates: the replacement anchor (the sidebar's Add
  // Filter button) mounts in the same commit and re-points the popover.
  const addFilterOpenRef = useRef(false);
  useEffect(() => { addFilterOpenRef.current = addFilterOpen; }, [addFilterOpen]);
  const setAddFilterAnchorEl = useCallback((el) => {
    if (el == null && addFilterOpenRef.current) return;
    setAddFilterAnchorElRaw(el);
  }, []);

  // Self-managed: open sidebar when the first filter is added from a no-filter state.
  // Tracks the previous value to detect the false → true transition only.
  const prevHasFiltersToClear = useRef(false);
  useEffect(() => {
    if (showFilterModuleV3 && !prevHasFiltersToClear.current && hasFiltersToClear) {
      setShowSideBarLeft(true);  // first filter added → open sidebar
    }
    if (showFilterModuleV3 && prevHasFiltersToClear.current && !hasFiltersToClear) {
      setShowSideBarLeft(false); // all filters cleared → collapse sidebar
    }
    prevHasFiltersToClear.current = hasFiltersToClear;
  }, [hasFiltersToClear, showFilterModuleV3]);

  const content = contentError ? (
    <ErrorStates errorResponseObject={contentError} />
  ) : (
    <ListingModule
      status={contentStatus}
      data={contentData}
      emptyPageConfigSearch={emptyPageConfigSearch}
      sectionTitle={sectionTitle}
      sectionLink={sectionLink}
      totalCount={showCount ? totalCount : undefined}
      listingStyle={listingStyle}
      listingConfig={listingConfig}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={contentIsFetchingNextPage}
      activeSort={activeSort}
      setActiveSort={setActiveSort}
      hasNextPage={hasNextPage}
      ListingWrapper={ListingWrapper}
      enableContainerQuery={enableContainerQuery}
      contentTypes={contentTypes}
      inTCI={inTCI}
      builderProps={builderProps}
      getDataToBeExported={getDataToBeExported}
      hasAccessToDowloadCsv={!!getDataToBeExported}
      
    />
  );
  
  const contentConfig = {
    layout: listingStyle === "TableStyle" ? 'fullWidth' : "contained",
    content
  };
  
  const filter = showFilters && (
    <FilterModule
      totalResult={totalCount}
      setActiveFilters={setActiveFilters}
      activeFilters={activeFilters}
      presetActiveFilters={presetActiveFilters}
      filterConfig={filterConfig}
      contentTypes={contentTypes}
      usedIn={"ListingPage"}
      storeKey={collectionId}
      isMobile={isMobile}
      mobileHeader={isMobile && (
        <FilterModuleHeader
          style={{
            padding: '1.2rem 1.6rem',
            borderBottom: `1px solid ${colors2.grey10}`
          }}
          isMobile={isMobile}
          showSideBarLeft={showSideBarLeft}
          onClose={() => setShowSideBarLeft(false)}
          setActiveFilters={setActiveFilters}
        />
      )}
      addFilterAnchorEl={addFilterAnchorEl}
      addFilterLightboxOpen={addFilterOpen}
      onAddFilterLightboxOpenChange={setAddFilterOpen}
      onClearFilters={onClearFilters}
      onHasFiltersToClear={setHasFiltersToClear}
    />
  );

  return (
    <>
      {(!!filter || !!showSortDropdown || !!showSearchInput || enableListingStyleSwitcher === true) && (
        <ActionsPanel
          style={{
            zIndex: 101,
            ...(!coverConfigExists ? { backgroundColor: colors2.white } : {})
          }}
          actionRef={actionRef}
          filtersExist={!!filter}
          isMobile={isMobile}
          setShowSideBarLeft={setShowSideBarLeft}
          showSideBarLeft={showSideBarLeft}
          Wrapper={!!filter && "Wrapper1500"}
          actionsPanelStickyTop={actionsPanelStickyTop}
          showDownloadDataButton={!!getDataToBeExported}
          getDataToBeExported={getDataToBeExported}
          
          // Sort related
          showSortDropdown={showSortDropdown}
          activeSort={activeSort}
          setActiveSort={setActiveSort}
          sortConfig={sortConfig}

          // Search related
          showSearchInput={showSearchInput}
          searchTermInputText={searchTermInputText}
          handleSearch={handleSearch}
          
          //listing style switcher related
          enableListingStyleSwitcher={enableListingStyleSwitcher}
          listingStyle={listingStyle}
          setListingStyle={setListingStyle}

          // External filter control (self-managed tenants)
          activeFiltersCount={activeFilters?.length || 0}
          activeFilters={activeFilters}
          setAddFilterAnchorEl={setAddFilterAnchorEl}
          setAddFilterOpen={setAddFilterOpen}
          clearFilters={clearFilters}
          hasFiltersToClear={hasFiltersToClear}
        />
      )}
      
      <Shell
        contentConfig={contentConfig}
        filter={filter}
        isMobile={isMobile}
        ONLY_DESKTOP_filtersSidebarStickyTop={ONLY_DESKTOP_filtersSidebarStickyTop}
        stickyTopToViewportTopGap={stickyTopToViewportTopGap}
        showSideBarLeft={showSideBarLeft}
        setShowSideBarLeft={setShowSideBarLeft}
      />
    </>
  );
};